import { AmountDisplay } from "@/app/shared/ui/amount-display";
import {
  useBulkDeleteTransactions,
  useBulkUpdateTransactions,
  useCreateTransaction,
  useDeleteTransaction,
  useUpdateTransaction,
} from "@/hooks/api";
import { formatDate } from "@/lib/date.utils";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import {
  type Account,
  type Category,
  type Tag,
  type Transaction,
  type TransactionFormData,
  TransactionType,
} from "@/types";
import { Button } from "@groot/ui/button";
import { Card } from "@groot/ui/card";
import { Checkbox } from "@groot/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@groot/ui/dropdown-menu";
import { Input } from "@groot/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  tableColumnHeaderClass,
} from "@groot/ui/table";
import dayjs from "dayjs";
import {
  ArrowDown,
  ArrowUp,
  Calendar,
  CircleCheck,
  MoreHorizontal,
  Pencil,
  Search,
  StickyNote,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { DeleteConfirmModal } from "./DeleteConfirmModal";
import { InlineCategorySelect, getGroupedCategories } from "./InlineCategorySelect";
import { InlineEditableCell } from "./InlineEditableCell";

interface InlineTransactionTableProps {
  transactions: Transaction[];
  accounts: Account[];
  categories: Category[];
  tags: Tag[];
  isLoading?: boolean;
  onEdit?: (transaction: Transaction) => void;
  showAccountColumn?: boolean;
  startDate?: string;
  endDate?: string;
  /** Server-side sorting (Actual-style sortable column headers) */
  sortBy?: "date" | "amount" | "createdAt";
  sortOrder?: "asc" | "desc";
  onSort?: (sortBy: "date" | "amount", sortOrder: "asc" | "desc") => void;
  /** Inline add-transaction row (Actual-style "Add New") */
  isAdding?: boolean;
  onCancelAdd?: () => void;
  defaultAccountId?: number;
}

type FieldName = "date" | "description" | "notes" | "category" | "payment" | "deposit" | "cleared";

const FIELDS: FieldName[] = [
  "date",
  "description",
  "notes",
  "category",
  "payment",
  "deposit",
  "cleared",
];

const headerCellClass = cn(
  tableColumnHeaderClass,
  "px-2 py-2 text-left align-middle whitespace-nowrap border-b border-border bg-background",
);

// ─── Resizable columns ────────────────────────────────────────────────────────

type ResizableColumn =
  | "date"
  | "account"
  | "description"
  | "notes"
  | "category"
  | "payment"
  | "deposit";

// Persisted per browser so widths survive reloads and apply across every page
// that renders this table (Transactions, Account detail, Card transactions).
const COLUMN_WIDTHS_KEY = "nesins.txnTable.columnWidths";

const DEFAULT_COLUMN_WIDTHS: Record<ResizableColumn, number> = {
  date: 126,
  account: 150,
  description: 300,
  notes: 180,
  category: 160,
  payment: 110,
  deposit: 110,
};

const MIN_COLUMN_WIDTHS: Record<ResizableColumn, number> = {
  date: 100,
  account: 100,
  description: 120,
  notes: 80,
  category: 100,
  payment: 80,
  deposit: 80,
};

const MAX_COLUMN_WIDTH = 640;

const clampColumnWidth = (column: ResizableColumn, width: number) =>
  Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTHS[column], Math.round(width)));

// Non-resizable columns: select-all (36) + cleared (44) + actions (44)
const FIXED_COLUMNS_WIDTH = 124;

// When space is tight, Description borrows from these (in order, down to
// their minimums) until it reaches this width.
const DESCRIPTION_FIT_TARGET = 200;
const DESCRIPTION_DONORS: ResizableColumn[] = ["notes", "category", "account"];

/**
 * First-use widths for a table `available` px wide: Description takes the
 * leftover space; when there is too little (e.g. 1440px with both sidebars
 * open), Notes, Category, then Account shrink toward their minimums so the
 * amount columns stay on screen instead of scrolling sideways.
 */
export function fitColumnWidths(
  available: number,
  showAccountColumn: boolean,
): Record<ResizableColumn, number> {
  const widths = { ...DEFAULT_COLUMN_WIDTHS };
  const otherColumns: ResizableColumn[] = showAccountColumn
    ? ["date", "account", "notes", "category", "payment", "deposit"]
    : ["date", "notes", "category", "payment", "deposit"];
  const othersWidth = otherColumns.reduce((sum, col) => sum + widths[col], 0);
  let description = available - othersWidth - FIXED_COLUMNS_WIDTH;
  for (const col of DESCRIPTION_DONORS) {
    if (description >= DESCRIPTION_FIT_TARGET) break;
    if (!otherColumns.includes(col)) continue;
    const give = Math.min(
      widths[col] - MIN_COLUMN_WIDTHS[col],
      DESCRIPTION_FIT_TARGET - description,
    );
    widths[col] -= give;
    description += give;
  }
  widths.description = clampColumnWidth("description", description);
  return widths;
}

const loadColumnWidths = (): Record<ResizableColumn, number> => {
  const widths = { ...DEFAULT_COLUMN_WIDTHS };
  try {
    const raw = localStorage.getItem(COLUMN_WIDTHS_KEY);
    if (!raw) return widths;
    const saved = JSON.parse(raw) as Partial<Record<ResizableColumn, unknown>>;
    for (const column of Object.keys(DEFAULT_COLUMN_WIDTHS) as ResizableColumn[]) {
      const width = saved[column];
      if (typeof width === "number" && Number.isFinite(width)) {
        widths[column] = clampColumnWidth(column, width);
      }
    }
    return widths;
  } catch {
    return widths;
  }
};

// Drag handle on a column header's right edge (Actual-style column resizing).
// Pointer capture keeps the drag alive outside the handle and works for both
// mouse and touch. Double-click resets to the default width; arrow keys resize.
function ColumnResizer({
  column,
  columnWidth,
  onResize,
  onCommit,
  onReset,
}: {
  column: ResizableColumn;
  columnWidth: number;
  onResize: (column: ResizableColumn, width: number) => void;
  onCommit: () => void;
  onReset: (column: ResizableColumn) => void;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, width: 0 });

  const stopDragging = (commit: boolean) => {
    if (!isDragging) return;
    setIsDragging(false);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    if (commit) onCommit();
  };

  return (
    <>
      {/* biome-ignore lint/a11y/useSemanticElements: interactive resize handle (focusable, arrow-key operable), not a thematic break */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${column} column`}
        tabIndex={0}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          e.preventDefault();
          dragStartRef.current = { x: e.clientX, width: columnWidth };
          e.currentTarget.setPointerCapture(e.pointerId);
          setIsDragging(true);
          document.body.style.cursor = "col-resize";
          document.body.style.userSelect = "none";
        }}
        onPointerMove={(e) => {
          if (!isDragging) return;
          onResize(column, dragStartRef.current.width + (e.clientX - dragStartRef.current.x));
        }}
        onPointerUp={() => stopDragging(true)}
        onPointerCancel={() => stopDragging(false)}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onReset(column);
        }}
        onKeyDown={(e) => {
          const step = e.shiftKey ? 32 : 8;
          if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
            e.preventDefault();
            const delta = e.key === "ArrowLeft" ? -step : step;
            onResize(column, columnWidth + delta);
            onCommit();
          }
        }}
        className="group/resizer absolute inset-y-0 right-0 z-20 flex w-1.5 cursor-col-resize touch-none items-center justify-center"
      >
        {/* Hit area stays invisible; the tick is the visible divider */}
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none w-px",
            isDragging
              ? "h-full bg-primary/40"
              : "h-4 bg-border group-hover/resizer:h-full group-hover/resizer:bg-muted-foreground/40",
          )}
        />
      </div>
    </>
  );
}

// ─── Payment / Deposit editable cell ────────────────────────────────────────

interface AmountCellProps {
  transaction: Transaction;
  mode: "payment" | "deposit";
  focused?: boolean;
  onFocus?: () => void;
  onBlurComplete?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onSave: (txn: Transaction, mode: "payment" | "deposit", value: number) => void;
}

function AmountCell({
  transaction,
  mode,
  focused = false,
  onFocus,
  onBlurComplete,
  onKeyDown: externalKeyDown,
  onSave,
}: AmountCellProps) {
  const isDepositColumn = mode === "deposit";
  const matchesType = isDepositColumn
    ? transaction.type === TransactionType.CREDIT
    : transaction.type !== TransactionType.CREDIT;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = useCallback(() => {
    setEditValue(matchesType ? String(transaction.amount) : "");
    setIsEditing(true);
  }, [matchesType, transaction.amount]);

  // Auto-focus when focused prop changes to true (keyboard navigation)
  useEffect(() => {
    if (focused && !isEditing) {
      startEditing();
    }
  }, [focused, isEditing, startEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const finish = (save: boolean) => {
    const raw = editValue.trim();
    setIsEditing(false);
    onBlurComplete?.();
    if (!save || raw === "") return;
    const parsed = Number(raw);
    if (Number.isNaN(parsed) || parsed <= 0) return;
    if (matchesType && parsed === transaction.amount) return;
    onSave(transaction, mode, parsed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      finish(true);
      externalKeyDown?.(e);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      finish(false);
      return;
    }
    if (e.key === "Tab") {
      finish(true);
      externalKeyDown?.(e);
    }
  };

  if (!isEditing) {
    return (
      <div
        className="px-2 py-1 -mx-2 -my-1 rounded cursor-text hover:bg-muted/50 flex items-center justify-end min-w-0 min-h-[28px]"
        onClick={() => {
          startEditing();
          onFocus?.();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            startEditing();
            onFocus?.();
          }
        }}
      >
        {matchesType && (
          <AmountDisplay
            amount={transaction.amount}
            currencyCode={transaction.currencyCode}
            type={transaction.type}
            className="min-w-0 truncate text-sm tabular-nums"
          />
        )}
      </div>
    );
  }

  // Seamless edit (Actual-style): ghost preserves cell metrics, input overlays
  return (
    <div className="relative">
      <div
        className="px-2 py-1 -mx-2 -my-1 flex items-center justify-end min-h-[28px] invisible"
        aria-hidden="true"
      >
        <span className="text-sm tabular-nums">
          {matchesType ? String(transaction.amount) : "0.00"}
        </span>
      </div>
      <Input
        ref={inputRef}
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => finish(true)}
        className={cn(
          // z-50: float above the sticky table header when editing the first row
          "absolute -inset-x-2 -inset-y-1 z-50 px-2 text-sm text-right border-primary bg-background",
          // Spreadsheet feel: no native number spinners (like Actual)
          "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
        )}
        style={{ height: "auto", width: "auto" }}
        placeholder="0.00"
      />
    </div>
  );
}

// ─── Category picker for the add-transaction row ────────────────────────────

function AddRowCategorySelect({
  value,
  categories,
  onChange,
}: {
  value: number | null;
  categories: Category[];
  onChange: (categoryId: number | null) => void;
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const {
    groupCategories: groups,
    ungroupedCategories,
    childCategoriesMap,
  } = useMemo(() => getGroupedCategories(categories, searchTerm), [categories, searchTerm]);

  return (
    <Select
      value={value?.toString() ?? "none"}
      onValueChange={(v) => onChange(v === "none" ? null : Number(v))}
    >
      <SelectTrigger size="sm" className="w-full text-sm">
        <SelectValue placeholder="Category" />
      </SelectTrigger>
      {/* position="popper": keep the panel anchored to the trigger inside the
          scrollable table (item-aligned positioning jumps on container scroll). */}
      <SelectContent position="popper" align="start">
        <div className="flex items-center border-b px-3 pb-2">
          <Search className="size-4 shrink-0 opacity-50 mr-2" />
          <Input
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-8 w-full border-0 bg-transparent p-0 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>
        <div className="max-h-[300px] overflow-y-auto">
          <SelectItem value="none">
            <span className="text-muted-foreground">None</span>
          </SelectItem>
          {ungroupedCategories.map((category) => (
            <SelectItem key={category.id} value={category.id.toString()}>
              {category.name}
            </SelectItem>
          ))}
          {groups.map((group) => (
            <div key={group.id}>
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 mt-1">
                {group.name}
              </div>
              {childCategoriesMap[group.id]?.map((child) => (
                <SelectItem key={child.id} value={child.id.toString()} className="pl-6">
                  {child.name}
                </SelectItem>
              ))}
            </div>
          ))}
        </div>
      </SelectContent>
    </Select>
  );
}

// ─── Actual-style inline "new transaction" row ──────────────────────────────

interface NewTransactionRowProps {
  accounts: Account[];
  categories: Category[];
  showAccountColumn: boolean;
  defaultAccountId?: number;
  onClose: () => void;
}

function NewTransactionRow({
  accounts,
  categories,
  showAccountColumn,
  defaultAccountId,
  onClose,
}: NewTransactionRowProps) {
  const createMutation = useCreateTransaction();
  const [date, setDate] = useState(() => formatDate(dayjs()));
  const [accountId, setAccountId] = useState<number | undefined>(
    defaultAccountId ?? accounts[0]?.id,
  );
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [payment, setPayment] = useState("");
  const [deposit, setDeposit] = useState("");
  const descriptionRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    descriptionRef.current?.focus();
  }, []);

  const handleSave = () => {
    const paymentValue = Number(payment);
    const depositValue = Number(deposit);
    const hasPayment = payment.trim() !== "" && !Number.isNaN(paymentValue) && paymentValue > 0;
    const hasDeposit = deposit.trim() !== "" && !Number.isNaN(depositValue) && depositValue > 0;

    if (!date) {
      toast({ variant: "destructive", description: "Enter a date" });
      return;
    }
    if (!accountId) {
      toast({ variant: "destructive", description: "Select an account" });
      return;
    }
    if (hasPayment && hasDeposit) {
      toast({
        variant: "destructive",
        description: "Enter either a payment or a deposit, not both",
      });
      return;
    }
    if (!hasPayment && !hasDeposit) {
      toast({ variant: "destructive", description: "Enter an amount" });
      return;
    }

    createMutation.mutate(
      {
        date,
        accountId,
        type: hasDeposit ? TransactionType.CREDIT : TransactionType.DEBIT,
        amount: hasDeposit ? depositValue : paymentValue,
        description: description.trim() || undefined,
        notes: notes.trim() || undefined,
        categoryId: categoryId ?? undefined,
      },
      {
        onSuccess: () => {
          // Keep date + account for rapid entry, clear the rest (Actual behavior)
          setDescription("");
          setNotes("");
          setCategoryId(null);
          setPayment("");
          setDeposit("");
          descriptionRef.current?.focus();
        },
      },
    );
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  const cellClass = "px-2 py-1 align-middle";

  return (
    <TableRow className="border-b border-border bg-muted/20">
      {/* Select placeholder */}
      <TableCell className="w-[36px]" />
      {/* Date */}
      <TableCell className={cellClass}>
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          onKeyDown={handleInputKeyDown}
          className="h-7 px-1 text-sm"
        />
      </TableCell>
      {/* Account */}
      {showAccountColumn && (
        <TableCell className={cellClass}>
          <Select value={accountId?.toString()} onValueChange={(v) => setAccountId(Number(v))}>
            <SelectTrigger size="sm" className="w-full text-sm">
              <SelectValue placeholder="Account" />
            </SelectTrigger>
            <SelectContent>
              {accounts.map((account) => (
                <SelectItem key={account.id} value={account.id.toString()}>
                  {account.accountName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TableCell>
      )}
      {/* Description */}
      <TableCell className={cellClass}>
        <Input
          ref={descriptionRef}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={handleInputKeyDown}
          className="h-7 text-sm"
          placeholder="Payee / description"
        />
      </TableCell>
      {/* Notes */}
      <TableCell className={cellClass}>
        <Input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onKeyDown={handleInputKeyDown}
          className="h-7 text-sm"
          placeholder="Notes"
        />
      </TableCell>
      {/* Category */}
      <TableCell className={cellClass}>
        <AddRowCategorySelect value={categoryId} categories={categories} onChange={setCategoryId} />
      </TableCell>
      {/* Payment */}
      <TableCell className={cellClass}>
        <Input
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={payment}
          onChange={(e) => setPayment(e.target.value)}
          onKeyDown={handleInputKeyDown}
          className="h-7 px-2 text-sm text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          placeholder="0.00"
        />
      </TableCell>
      {/* Deposit */}
      <TableCell className={cellClass}>
        <Input
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={deposit}
          onChange={(e) => setDeposit(e.target.value)}
          onKeyDown={handleInputKeyDown}
          className="h-7 px-2 text-sm text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          placeholder="0.00"
        />
      </TableCell>
      {/* Cleared placeholder */}
      <TableCell className="w-[44px]" />
      {/* Cancel */}
      <TableCell className="w-[44px] px-1 py-1 text-center">
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={onClose}
          aria-label="Cancel new transaction"
        >
          <X className="size-3.5 text-muted-foreground" />
        </Button>
      </TableCell>
      {/* Flex spacer: matches the header/data rows' trailing spacer column */}
      <TableCell aria-hidden="true" />
    </TableRow>
  );
}

// ─── Main table ─────────────────────────────────────────────────────────────

export function InlineTransactionTable({
  transactions,
  accounts,
  categories,
  isLoading,
  onEdit,
  showAccountColumn = true,
  startDate,
  endDate,
  sortBy,
  sortOrder,
  onSort,
  isAdding = false,
  onCancelAdd,
  defaultAccountId,
}: InlineTransactionTableProps) {
  const updateMutation = useUpdateTransaction();
  const deleteMutation = useDeleteTransaction();
  const bulkDeleteMutation = useBulkDeleteTransactions();
  const bulkUpdateMutation = useBulkUpdateTransactions();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<Transaction | null>(null);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);

  // Column widths (persisted; ref mirror lets the drag commit read latest)
  const [columnWidths, setColumnWidths] =
    useState<Record<ResizableColumn, number>>(loadColumnWidths);
  const columnWidthsRef = useRef(columnWidths);
  columnWidthsRef.current = columnWidths;
  const tableWrapRef = useRef<HTMLDivElement>(null);

  const handleColumnResize = useCallback((column: ResizableColumn, width: number) => {
    const next = {
      ...columnWidthsRef.current,
      [column]: clampColumnWidth(column, width),
    };
    columnWidthsRef.current = next;
    setColumnWidths(next);
  }, []);

  const commitColumnWidths = useCallback(() => {
    try {
      localStorage.setItem(COLUMN_WIDTHS_KEY, JSON.stringify(columnWidthsRef.current));
    } catch {
      // Storage unavailable (private browsing) — resizing still works in-session
    }
  }, []);

  const resetColumnWidth = useCallback(
    (column: ResizableColumn) => {
      handleColumnResize(column, DEFAULT_COLUMN_WIDTHS[column]);
      commitColumnWidths();
    },
    [handleColumnResize, commitColumnWidths],
  );

  // First use (no saved widths): fit the columns to the space on screen (see
  // fitColumnWidths). Re-runs when the table wrapper (mounts after
  // loading/empty states) appears.
  // biome-ignore lint/correctness/useExhaustiveDependencies: extra deps re-trigger measurement once the wrapper is actually in the DOM
  useLayoutEffect(() => {
    if (localStorage.getItem(COLUMN_WIDTHS_KEY)) return;
    const available = tableWrapRef.current?.clientWidth ?? 0;
    if (available <= 0) return;
    setColumnWidths(fitColumnWidths(available, showAccountColumn));
  }, [showAccountColumn, isLoading, transactions.length, isAdding]);

  // Selection state (spreadsheet-like multi-select)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const lastSelectedIndexRef = useRef<number | null>(null);
  const shiftHeldRef = useRef(false);

  // Keyboard navigation state
  const [focusedCell, setFocusedCell] = useState<{
    row: number;
    field: FieldName;
  } | null>(null);

  // Prune selection when the visible transactions change (page/filter change)
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visibleIds = new Set(transactions.map((t) => t.id));
      const next = new Set([...prev].filter((id) => visibleIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [transactions]);

  const hasSelection = selectedIds.size > 0;
  const allSelected = transactions.length > 0 && selectedIds.size >= transactions.length;
  const someSelected = !allSelected && transactions.some((t) => selectedIds.has(t.id));

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(transactions.map((t) => t.id)));
  };

  const handleRowSelect = (transactionId: number, rowIndex: number, shiftKey: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const anchor = lastSelectedIndexRef.current;
      if (shiftKey && anchor !== null && anchor !== rowIndex) {
        const start = Math.min(anchor, rowIndex);
        const end = Math.max(anchor, rowIndex);
        for (let i = start; i <= end; i++) {
          next.add(transactions[i].id);
        }
      } else if (next.has(transactionId)) {
        next.delete(transactionId);
      } else {
        next.add(transactionId);
      }
      return next;
    });
    if (!shiftKey) {
      lastSelectedIndexRef.current = rowIndex;
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Bulk category assignment state (search box inside the toolbar category picker)
  const [bulkCategorySearch, setBulkCategorySearch] = useState("");
  const { groupCategories, ungroupedCategories, childCategoriesMap } = useMemo(
    () => getGroupedCategories(categories, bulkCategorySearch),
    [categories, bulkCategorySearch],
  );

  const handleBulkSetReviewed = (isReviewed: boolean) => {
    const ids = [...selectedIds];
    bulkUpdateMutation.mutate(
      { transactionIds: ids, data: { isReviewed } },
      { onSuccess: () => clearSelection() },
    );
  };

  const handleBulkSetCategory = (value: string) => {
    const categoryId = value === "none" ? null : Number(value);
    const ids = [...selectedIds];
    bulkUpdateMutation.mutate(
      { transactionIds: ids, data: { categoryId } },
      { onSuccess: () => clearSelection() },
    );
  };

  const confirmBulkDelete = () => {
    const ids = [...selectedIds];
    bulkDeleteMutation.mutate(ids, {
      onSuccess: () => {
        setBulkDeleteModalOpen(false);
        clearSelection();
      },
    });
  };

  const handleOpenFullEdit = (transaction: Transaction) => {
    onEdit?.(transaction);
  };

  const handleDelete = async (transaction: Transaction) => {
    setTransactionToDelete(transaction);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!transactionToDelete) return;

    deleteMutation.mutate(transactionToDelete.id, {
      onSuccess: () => {
        setDeleteModalOpen(false);
        setTransactionToDelete(null);
      },
    });
  };

  const handleReviewToggle = async (transaction: Transaction) => {
    updateMutation.mutate({
      id: transaction.id,
      data: { isReviewed: !transaction.isReviewed },
    });
  };

  const handleInlineUpdate = (txn: Transaction, patch: Partial<TransactionFormData>) => {
    updateMutation.mutate({
      id: txn.id,
      data: {
        ...patch,
        // Editing any field implicitly marks an unreviewed transaction as reviewed
        ...(txn.isReviewed === false ? { isReviewed: true } : {}),
      },
    });
  };

  const handleAmountSave = (txn: Transaction, mode: "payment" | "deposit", value: number) => {
    handleInlineUpdate(txn, {
      amount: value,
      type: mode === "deposit" ? TransactionType.CREDIT : TransactionType.DEBIT,
    });
  };

  const getAccount = (accountId: number) => {
    return accounts.find((acc) => acc.id === accountId);
  };

  const handleCellFocus = useCallback((rowIndex: number, field: FieldName) => {
    setFocusedCell({ row: rowIndex, field });
  }, []);

  const handleKeyNavigation = useCallback(
    (e: React.KeyboardEvent, rowIndex: number, currentField: FieldName) => {
      const currentFieldIndex = FIELDS.indexOf(currentField);

      if (e.key === "Tab") {
        e.preventDefault();

        if (e.shiftKey) {
          // Move to previous cell
          if (currentFieldIndex > 0) {
            setFocusedCell({
              row: rowIndex,
              field: FIELDS[currentFieldIndex - 1],
            });
          } else if (rowIndex > 0) {
            // Move to last field of previous row
            setFocusedCell({
              row: rowIndex - 1,
              field: FIELDS[FIELDS.length - 1],
            });
          }
        } else {
          // Move to next cell
          if (currentFieldIndex < FIELDS.length - 1) {
            setFocusedCell({
              row: rowIndex,
              field: FIELDS[currentFieldIndex + 1],
            });
          } else if (rowIndex < transactions.length - 1) {
            // Move to first field of next row
            setFocusedCell({ row: rowIndex + 1, field: FIELDS[0] });
          }
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        // Move to next row, same field (like Google Sheets)
        if (rowIndex < transactions.length - 1) {
          setFocusedCell({ row: rowIndex + 1, field: currentField });
        }
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        if (rowIndex < transactions.length - 1) {
          setFocusedCell({ row: rowIndex + 1, field: currentField });
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (rowIndex > 0) {
          setFocusedCell({ row: rowIndex - 1, field: currentField });
        }
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (currentFieldIndex < FIELDS.length - 1) {
          setFocusedCell({
            row: rowIndex,
            field: FIELDS[currentFieldIndex + 1],
          });
        }
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (currentFieldIndex > 0) {
          setFocusedCell({
            row: rowIndex,
            field: FIELDS[currentFieldIndex - 1],
          });
        }
      }
    },
    [transactions.length],
  );

  const renderSortableHeader = (
    label: string,
    field: "date" | "amount",
    defaultDirection: "asc" | "desc",
    alignRight = false,
  ) => {
    if (!onSort) return <>{label}</>;
    const active = sortBy === field;
    return (
      <button
        type="button"
        onClick={() =>
          onSort(field, active ? (sortOrder === "asc" ? "desc" : "asc") : defaultDirection)
        }
        className={cn(
          "inline-flex items-center gap-1 hover:text-foreground",
          alignRight && "flex-row-reverse",
        )}
      >
        {label}
        {active &&
          (sortOrder === "asc" ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />)}
      </button>
    );
  };

  const renderResizableHead = (
    column: ResizableColumn,
    children: React.ReactNode,
    extraClass?: string,
  ) => (
    <TableHead
      className={cn(headerCellClass, "relative", extraClass)}
      style={{ width: columnWidths[column] }}
    >
      {children}
      <ColumnResizer
        column={column}
        columnWidth={columnWidths[column]}
        onResize={handleColumnResize}
        onCommit={commitColumnWidths}
        onReset={resetColumnWidth}
      />
    </TableHead>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="inline-block size-6 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" />
      </div>
    );
  }

  if (transactions.length === 0 && !isAdding) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Calendar className="size-10 text-muted-foreground/40 mb-3" />
        <p className="text-sm text-muted-foreground">No transactions found</p>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Card View */}
      <div className="md:hidden bg-background p-3 space-y-2">
        {transactions.map((transaction) => {
          const account = getAccount(transaction.accountId);
          const description =
            transaction.description || transaction.tempDescription || "No description";
          const category = categories.find((c) => c.id === transaction.categoryId);

          return (
            <Card
              key={transaction.id}
              onClick={() => handleOpenFullEdit(transaction)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleOpenFullEdit(transaction);
                }
              }}
              tabIndex={0}
              // biome-ignore lint/a11y/useSemanticElements: using div for complex interactive element
              role="button"
              size="sm"
              className="cursor-pointer p-3 active:bg-muted/50"
            >
              {showAccountColumn ? (
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {account?.iconUrl && (
                      <img
                        src={account.iconUrl}
                        alt={account.accountName}
                        className="size-4 rounded flex-shrink-0"
                      />
                    )}
                    <span className="text-sm font-medium truncate">{account?.accountName}</span>
                  </div>
                  <div className="text-sm whitespace-nowrap ml-2">
                    <AmountDisplay
                      amount={transaction.amount}
                      currencyCode={transaction.currencyCode}
                      type={transaction.type}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">
                    {dayjs(transaction.date).format("DD/MM/YYYY")}
                  </span>
                  <div className="text-sm whitespace-nowrap ml-2">
                    <AmountDisplay
                      amount={transaction.amount}
                      currencyCode={transaction.currencyCode}
                      type={transaction.type}
                    />
                  </div>
                </div>
              )}

              <div className="text-sm text-foreground mb-1">{description}</div>

              {transaction.notes?.trim() && (
                <div
                  className="mb-1 flex items-start gap-1.5 text-xs text-muted-foreground"
                  data-testid="transaction-card-notes"
                >
                  <StickyNote className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                  <span className="line-clamp-2 break-words">{transaction.notes}</span>
                </div>
              )}

              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <span>{dayjs(transaction.date).format("DD/MM/YYYY")}</span>
                  {category && (
                    <>
                      <span>•</span>
                      <span>{category.name}</span>
                    </>
                  )}
                </div>
                {transaction.isReviewed && (
                  <CircleCheck className="size-3.5 fill-success text-white" />
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Desktop Table View */}
      <div ref={tableWrapRef} className="hidden md:block bg-background">
        {/* Selection action bar (Actual-style bulk actions) */}
        {hasSelection && (
          <div className="flex items-center gap-1 px-3 py-1.5 bg-accent/60 border-b border-border">
            <span className="text-sm font-medium mr-2">{selectedIds.size} selected</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => handleBulkSetReviewed(true)}
            >
              Mark reviewed
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => handleBulkSetReviewed(false)}
            >
              Mark unreviewed
            </Button>
            <Select
              onValueChange={handleBulkSetCategory}
              onOpenChange={(open) => {
                if (!open) setBulkCategorySearch("");
              }}
            >
              <SelectTrigger
                size="sm"
                className="h-7 w-fit gap-1.5 text-xs"
                aria-label="Set category"
              >
                <SelectValue placeholder="Set category" />
              </SelectTrigger>
              <SelectContent>
                <div className="flex items-center border-b px-3 pb-2">
                  <Search className="size-4 shrink-0 opacity-50 mr-2" />
                  <Input
                    placeholder="Search categories..."
                    value={bulkCategorySearch}
                    onChange={(e) => setBulkCategorySearch(e.target.value)}
                    className="h-8 w-full border-0 bg-transparent p-0 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                  <SelectItem value="none">
                    <span className="text-muted-foreground">None</span>
                  </SelectItem>
                  {ungroupedCategories.length === 0 &&
                    groupCategories.length === 0 &&
                    bulkCategorySearch && (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No categories found for &ldquo;{bulkCategorySearch}
                        &rdquo;
                      </div>
                    )}
                  {ungroupedCategories.map((category) => (
                    <SelectItem key={category.id} value={category.id.toString()}>
                      {category.name}
                    </SelectItem>
                  ))}
                  {groupCategories.map((group) => (
                    <div key={group.id}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 mt-1">
                        {group.name}
                      </div>
                      {childCategoriesMap[group.id]?.map((child) => (
                        <SelectItem key={child.id} value={child.id.toString()} className="pl-6">
                          {child.name}
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </div>
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-destructive hover:text-destructive"
              onClick={() => setBulkDeleteModalOpen(true)}
            >
              <Trash2 className="size-3.5" />
              Delete
            </Button>
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={clearSelection}
              aria-label="Clear selection"
            >
              <X className="size-3.5" />
            </Button>
          </div>
        )}

        {/* table-fixed: header widths are authoritative, so drag-resizing a
            column gives it an exact pixel width */}
        <Table className="w-full table-fixed">
          <TableHeader className="sticky top-0 z-10">
            <TableRow>
              {/* Select all */}
              <TableHead className={cn(headerCellClass, "w-[36px] px-3")}>
                <Checkbox
                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all transactions"
                />
              </TableHead>
              {renderResizableHead("date", renderSortableHeader("Date", "date", "desc"))}
              {showAccountColumn && renderResizableHead("account", "Account")}
              {renderResizableHead("description", "Description")}
              {renderResizableHead("notes", "Notes")}
              {renderResizableHead("category", "Category")}
              {renderResizableHead(
                "payment",
                renderSortableHeader("Payment", "amount", "asc", true),
                "text-right",
              )}
              {renderResizableHead(
                "deposit",
                renderSortableHeader("Deposit", "amount", "desc", true),
                "text-right",
              )}
              <TableHead
                className={cn(headerCellClass, "w-[44px] text-center")}
                title="Cleared (reviewed)"
              >
                ✓
              </TableHead>
              <TableHead className={cn(headerCellClass, "w-[44px]")} />
              {/* Flex spacer: absorbs leftover width so resized columns keep
                  their exact size */}
              <TableHead className={headerCellClass} aria-hidden="true" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Actual-style inline add row */}
            {isAdding && onCancelAdd && (
              <NewTransactionRow
                accounts={accounts}
                categories={categories}
                showAccountColumn={showAccountColumn}
                defaultAccountId={defaultAccountId}
                onClose={onCancelAdd}
              />
            )}

            {transactions.map((transaction, rowIndex) => {
              const account = getAccount(transaction.accountId);
              const isCreditTxn = transaction.type === TransactionType.CREDIT;
              const description = transaction.description || transaction.tempDescription || "";
              const isFocusedRow = focusedCell?.row === rowIndex;
              const isSelected = selectedIds.has(transaction.id);

              return (
                <TableRow
                  key={transaction.id}
                  className={cn(
                    "group border-b border-border/60 hover:bg-muted/40",
                    isFocusedRow && !isSelected && "bg-muted/40",
                    isSelected && "bg-accent hover:bg-accent",
                  )}
                >
                  {/* Selection checkbox */}
                  <TableCell className="w-[36px] px-3 py-1 align-middle">
                    <div
                      onClickCapture={(e) => {
                        shiftHeldRef.current = e.shiftKey;
                      }}
                      className={cn(
                        "flex items-center justify-center transition-opacity",
                        isSelected || hasSelection
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-100",
                      )}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() =>
                          handleRowSelect(transaction.id, rowIndex, shiftHeldRef.current)
                        }
                        aria-label={`Select transaction ${description || transaction.id}`}
                      />
                    </div>
                  </TableCell>

                  {/* Date - Inline editable */}
                  <TableCell className="px-2 py-1 text-sm align-middle">
                    <InlineEditableCell
                      value={dayjs(transaction.date).format("YYYY-MM-DD")}
                      onSave={(value) => handleInlineUpdate(transaction, { date: String(value) })}
                      type="date"
                      displayFormatter={(val) => dayjs(val as string).format("DD/MM/YYYY")}
                      focused={focusedCell?.row === rowIndex && focusedCell?.field === "date"}
                      onFocus={() => handleCellFocus(rowIndex, "date")}
                      onBlurComplete={() => setFocusedCell(null)}
                      onKeyDown={(e) => handleKeyNavigation(e, rowIndex, "date")}
                    />
                  </TableCell>

                  {/* Account - Read-only, links to account detail */}
                  {showAccountColumn && (
                    <TableCell className="px-2 py-1 align-middle">
                      {account && (
                        <Link
                          to={`/accounts/${account.id}${startDate && endDate ? `?startDate=${startDate}&endDate=${endDate}` : ""}`}
                          className="flex items-center gap-1.5 hover:underline"
                        >
                          {account.iconUrl && (
                            <img
                              loading="lazy"
                              width={16}
                              height={16}
                              alt={`${account.accountName} icon`}
                              src={account.iconUrl}
                              className="rounded shrink-0"
                            />
                          )}
                          <span className="min-w-0 truncate text-sm">{account.accountName}</span>
                        </Link>
                      )}
                    </TableCell>
                  )}

                  {/* Description - Inline editable with expanded input */}
                  <TableCell className="px-2 py-1 align-middle">
                    <InlineEditableCell
                      value={description}
                      onSave={(value) =>
                        handleInlineUpdate(transaction, {
                          description: String(value),
                        })
                      }
                      type="text"
                      placeholder="No description"
                      className={cn({ "text-credit": isCreditTxn })}
                      focused={
                        focusedCell?.row === rowIndex && focusedCell?.field === "description"
                      }
                      onFocus={() => handleCellFocus(rowIndex, "description")}
                      onBlurComplete={() => setFocusedCell(null)}
                      onKeyDown={(e) => handleKeyNavigation(e, rowIndex, "description")}
                      expandOnEdit={true}
                    />
                  </TableCell>

                  {/* Notes - Inline editable with expanded input */}
                  <TableCell className="px-2 py-1 align-middle">
                    <InlineEditableCell
                      value={transaction.notes || ""}
                      onSave={(value) =>
                        handleInlineUpdate(transaction, {
                          notes: String(value),
                        })
                      }
                      type="text"
                      placeholder="Add notes..."
                      focused={focusedCell?.row === rowIndex && focusedCell?.field === "notes"}
                      onFocus={() => handleCellFocus(rowIndex, "notes")}
                      onBlurComplete={() => setFocusedCell(null)}
                      onKeyDown={(e) => handleKeyNavigation(e, rowIndex, "notes")}
                      expandOnEdit={true}
                    />
                  </TableCell>

                  {/* Category - Inline editable */}
                  <TableCell className="px-2 py-1 align-middle">
                    <InlineCategorySelect
                      value={transaction.categoryId || null}
                      categories={categories}
                      onSave={(categoryId) => handleInlineUpdate(transaction, { categoryId })}
                      focused={focusedCell?.row === rowIndex && focusedCell?.field === "category"}
                      onFocus={() => handleCellFocus(rowIndex, "category")}
                      onBlurComplete={() => setFocusedCell(null)}
                    />
                  </TableCell>

                  {/* Payment - Inline editable (debit) */}
                  <TableCell className="px-2 py-1 align-middle">
                    <AmountCell
                      transaction={transaction}
                      mode="payment"
                      focused={focusedCell?.row === rowIndex && focusedCell?.field === "payment"}
                      onFocus={() => handleCellFocus(rowIndex, "payment")}
                      onBlurComplete={() => setFocusedCell(null)}
                      onKeyDown={(e) => handleKeyNavigation(e, rowIndex, "payment")}
                      onSave={handleAmountSave}
                    />
                  </TableCell>

                  {/* Deposit - Inline editable (credit) */}
                  <TableCell className="px-2 py-1 align-middle">
                    <AmountCell
                      transaction={transaction}
                      mode="deposit"
                      focused={focusedCell?.row === rowIndex && focusedCell?.field === "deposit"}
                      onFocus={() => handleCellFocus(rowIndex, "deposit")}
                      onBlurComplete={() => setFocusedCell(null)}
                      onKeyDown={(e) => handleKeyNavigation(e, rowIndex, "deposit")}
                      onSave={handleAmountSave}
                    />
                  </TableCell>

                  {/* Cleared - Inline toggle */}
                  <TableCell className="w-[44px] px-1 py-1 text-center align-middle">
                    <button
                      type="button"
                      onClick={() => handleReviewToggle(transaction)}
                      onFocus={() => handleCellFocus(rowIndex, "cleared")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleReviewToggle(transaction);
                        } else {
                          handleKeyNavigation(e, rowIndex, "cleared");
                        }
                      }}
                      className="inline-flex items-center justify-center p-0.5 rounded hover:bg-muted cursor-pointer"
                      aria-label={
                        transaction.isReviewed ? "Mark as unreviewed" : "Mark as reviewed"
                      }
                    >
                      <CircleCheck
                        className={cn("size-4", {
                          "fill-success text-white": transaction.isReviewed,
                          "text-muted-foreground/40": !transaction.isReviewed,
                        })}
                      />
                    </button>
                  </TableCell>

                  {/* Actions */}
                  <TableCell className="w-[44px] px-1 py-1 align-middle">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-xs">
                          <MoreHorizontal className="size-3.5 text-muted-foreground" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenFullEdit(transaction)}>
                          <Pencil className="size-4" />
                          Edit Details
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDelete(transaction)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="size-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                  {/* Flex spacer: absorbs leftover width (fixed table layout) */}
                  <TableCell aria-hidden="true" />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation Modal (single) */}
      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        itemName={transactionToDelete?.description || "this transaction"}
        onConfirm={confirmDelete}
        onClose={() => {
          setDeleteModalOpen(false);
          setTransactionToDelete(null);
        }}
      />

      {/* Delete Confirmation Modal (bulk) */}
      <DeleteConfirmModal
        isOpen={bulkDeleteModalOpen}
        itemName={`${selectedIds.size} selected transaction${selectedIds.size === 1 ? "" : "s"}`}
        onConfirm={confirmBulkDelete}
        onClose={() => setBulkDeleteModalOpen(false)}
      />
    </>
  );
}
